import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { LocationFacade } from '@app/modules/location/facade/location.facade';
import { map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Subject } from 'rxjs';
import * as moment from 'moment';
import 'moment-duration-format';
import {
  PathFeature,
  PathStatusTypes,
  PathTimeLineStatus,
  TimeLinePoint,
  PoweredOffPoint,
  AdditionalPoint,
  TimeLine,
  TimeLinePathPoint
} from '@app/modules/location/models/path-api.model';
import { ResourceLoadState } from '@app/store/filters/models/resource-load.state';
import { LeafletService } from '@app/modules/location/services/leaflet.service';
import { LeafletMarkerAsset } from '@app/modules/location/models/leaflet-marker.asset';
import { EventTrackingFacade, TrackedEvents } from '@app/modules/location/facade/event-tracking.facade';
import { formatTimestamp } from '@app/modules/shared/utilities/utilities';
import { LocalizationService } from '@app/services/localization.service';
import { TranslateService } from '@livemaps/i18n';
import { EnvironmentService } from '@app/services/environment.service';
import { DriverFacade } from '@app/modules/driver/facade/driver.facade';
import { DriverProfile } from '@app/modules/driver/models/driver.models';
import { SettingsFacade } from '@app/modules/settings/facade/settings.facade';
import { Translations } from '@app/core/services/i18n/translations.service';

@Component({
  selector: 'app-timeline-control',
  templateUrl: './timeline-control.component.html',
  styleUrls: ['./timeline-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimelineControlComponent implements OnDestroy, OnInit {
  constructor(
    public locationFacade: LocationFacade,
    public leafletService: LeafletService,
    public readonly driverFacade: DriverFacade,
    public readonly settingsFacade: SettingsFacade,
    private changeDetectorRef: ChangeDetectorRef,
    private eventTrackingFacade: EventTrackingFacade,
    private envService: EnvironmentService,
    private localizationService: LocalizationService,
    public translateService: TranslateService,
    public translations: Translations
  ) {}

  formatTemplateTimestamp = formatTimestamp;
  segmentSelected: boolean = false;
  onDestroy$ = new Subject<void>();
  timeFormat = 'HH:mm';
  startTimeStamp: string = '';
  endTimeStamp: string = '';
  minIdleSec: number = 30000;
  loading = ResourceLoadState.LOADING;
  loaded = ResourceLoadState.LOAD_SUCCESSFUL;
  loadFailure = ResourceLoadState.LOAD_FAILURE;
  points: TimeLinePoint[];
  pointsZoomAux: TimeLinePoint[];
  hoverIndex: number;
  showStartTime: boolean = true;
  showEndTime: boolean = true;
  hoverEventCaptured = false;
  pointStartZoom: number = 0;
  startTimeZoom: string = '';
  pointEndZoom: number = 0;
  pointOnhoverZoom: number = 0;
  zoomEnable: boolean = false;
  selectEnable: boolean = false;
  settings$ = this.settingsFacade.getDisplaySettings();

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  getLocalizedTimestamp(timestamp: string) {
    return this.localizationService.getLocalizedTime(timestamp, { date: false, seconds: false });
  }
  ngOnInit() {
    this.settingsFacade.getDisplaySettings();
    this.getPathPoints().subscribe((timeLinePathPoint: TimeLinePathPoint) => {
      this.reset();
      this.segmentSelected = timeLinePathPoint.segmentSelected;
      if (this.segmentSelected) {
        this.handleSegmentPoints(timeLinePathPoint);
      } else {
        this.handlePoints(timeLinePathPoint.pathPoints);
        this.pointsZoomAux = [...timeLinePathPoint.pathPoints];
      }
    });

    this.onMouseOver().subscribe((feature: PathFeature) => {
      if (this.points && feature && feature.properties) {
        this.hoverIndex = this.points.findIndex(x => x.properties.id === feature.properties.id);
        this.toggleTime();
        this.changeDetectorRef.detectChanges();
      }
    });
  }
  onMouseOver() {
    return this.leafletService.onMouseover$.pipe(takeUntil(this.onDestroy$));
  }
  getPathPoints() {
    return this.leafletService.timeLinePoints$.pipe(
      withLatestFrom(this.driverFacade.getDriverAssetProfile()),
      map(([timeLine, driverProfile]: [TimeLine, DriverProfile[]]) => {
        const pathPoints = timeLine.pathPoints;
        const timeLinePoints: TimeLinePoint[] = pathPoints.reduce((acc: TimeLinePoint[], el, i: number) => {
          const element = { ...el, properties: { ...el.properties } };

          const currentDriverProfile = this.driverFacade.getDriverProfileAtTimestamp(
            driverProfile,
            element.properties.timestamp
          );
          element.properties.driverProfile = currentDriverProfile;
          if (i > 0) {
            const prevDriverId = acc[i - 1]?.properties?.driverProfile?.id;
            element.properties.driverChange = prevDriverId !== currentDriverProfile?.id;
          }

          const point: TimeLinePoint = this.createTimeLinePoint(element, this.envService);
          if (element.properties.powerOn) {
            point.setStatusAndMarker();
          }
          if (point.status == PathTimeLineStatus.IDLE) {
            point.checkMinIdleTime(pathPoints[i + 1]);
          }
          if (point.isFirst) {
            this.startTimeStamp = this.getLocalizedTimestamp(point.timestamp);
          }
          if (point.isLast) {
            this.endTimeStamp = this.getLocalizedTimestamp(point.timestamp);
          }
          acc.push(point);
          return acc;
        }, []);
        return { ...timeLine, pathPoints: timeLinePoints };
      }),
      takeUntil(this.onDestroy$)
    );
  }

  createTimeLinePoint = (timeLinePoint, envService?) => ({
    ...timeLinePoint,
    powerOn: timeLinePoint.properties.powerOn,
    state: timeLinePoint.properties.state,
    timestamp: timeLinePoint.properties.timestamp,
    status: PathTimeLineStatus.POWERED_OFF,
    markerSrc: LeafletMarkerAsset.POWERED_OFF,
    driverStateGyph: this.getActivityGlyph(timeLinePoint.properties.driverState),
    setStatusAndMarker() {
      if (timeLinePoint.properties.state == PathStatusTypes.DRIVING) {
        this.status = PathTimeLineStatus.IN_MOTION;
        this.markerSrc = LeafletMarkerAsset.IN_MOTION_VDO;
      } else {
        this.status = PathTimeLineStatus.IDLE;
        this.markerSrc = LeafletMarkerAsset.IDLING;
      }
      return this;
    },
    checkMinIdleTime(nextPoint) {
      const diff = moment(nextPoint?.properties?.timestamp).diff(moment(timeLinePoint.properties.timestamp));
      if (diff < this.minIdleSec) {
        this.status = PathTimeLineStatus.IN_MOTION;
        this.markerSrc = LeafletMarkerAsset.IN_MOTION_VDO;
      }
      return this;
    }
  });

  onHover(el: TimeLinePoint, i: number) {
    this.hoverIndex = i;
    this.toggleTime();
    this.leafletService.onHoverTimeline(el);
    if (!this.hoverEventCaptured) {
      this.eventTrackingFacade.trackEvent(TrackedEvents.TIMELINE);
      this.hoverEventCaptured = true;
    }
    if (this.selectEnable) {
      this.selectZoomArea(i);
    }
  }
  mouseDown(el: TimeLinePoint, i: number) {
    this.pointStartZoom = i;
    this.startTimeZoom = this.getLocalizedTimestamp(el.properties.timestamp);
    this.selectEnable = true;
  }
  mouseUp(i: number) {
    if (this.selectEnable) {
      this.pointEndZoom = i;
      let zoomPoints: TimeLinePoint[] = [];
      this.selectEnable = false;
      this.pointOnhoverZoom = 0;
      if (this.pointStartZoom <= this.pointEndZoom) {
        for (let i = this.pointStartZoom; i <= this.pointEndZoom; i++) {
          let pointIndex = document.getElementById(i.toString());
          pointIndex.classList.remove('zoom-select-area');
          zoomPoints.push(this.points[i]);
        }
      } else {
        for (let i = this.pointEndZoom; i <= this.pointStartZoom; i++) {
          let pointIndex = document.getElementById(i.toString());
          pointIndex.classList.remove('zoom-select-area');
          zoomPoints.push(this.points[i]);
        }
      }
      this.segmentSelected = true;
      this.handlePoints(zoomPoints);
      this.zoomEnable = true;
      this.leafletService.zoomToFeatures(zoomPoints);
    }
  }
  handlePoints(points) {
    const additionalPoints: AdditionalPoint[] = [];
    const whiskers = this.getWhiskersIndexes([...points]);
    // points = points.slice(whiskers[0], whiskers[1]);
    if (whiskers[1] == points.length - 1) {
      // if it's the last point we call slice without the end argument or else it gets cut off
      points = points.slice(whiskers[0]);
    } else {
      points = points.slice(whiskers[0], whiskers[1] + 1);
    }
    this.handleStartEndTime(points);
    const poweredOffPoints: number[] = this.indexOfAll(points, 'timeSincePreviousTrip');
    const poweredOffPtObjArray: PoweredOffPoint[] = poweredOffPoints?.map((pt: number) => {
      return { index: pt, point: points[pt - 1], nextPoint: points[pt] };
    });
    poweredOffPtObjArray?.forEach(obj => {
      const additionalPt: string[] = this.splitTime(
        moment.utc(obj.point.timestamp),
        moment.utc(obj.nextPoint.timestamp)
      );
      const newPoints: TimeLinePoint[] = additionalPt?.map((pt: string) => {
        const properties = { ...obj.point.properties, timestamp: pt };
        return { ...obj.point, properties: properties, timestamp: pt };
      });
      additionalPoints.push({ index: obj.index, points: newPoints });
    });
    this.addPoweredOffPoints(points, additionalPoints);
  }

  addPoweredOffPoints(points: TimeLinePoint[], additionalPoints: AdditionalPoint[]) {
    additionalPoints = additionalPoints.reverse();
    additionalPoints
      .filter(el => el && el.points)
      .forEach((el: AdditionalPoint) => {
        points.splice(el.index - 1, 1, ...el.points);
      });
    this.points = points;
    this.changeDetectorRef.detectChanges();
  }

  splitTime = (startTime, endTime, interval = 60): string[] => {
    const result = [startTime.format()];
    let time = startTime.add(interval, 's');
    while (time.isBetween(startTime, endTime, undefined, [])) {
      result.push(moment.utc(time).format());
      time = time.add(interval, 's');
    }
    return result;
  };

  indexOfAll = (arr: TimeLinePoint[], key: string) => arr.reduce((acc, el, i) => (el[key] ? [...acc, i] : acc), []);

  handleStartEndTime(pathPoints: TimeLinePoint[]) {
    this.startTimeStamp = this.getLocalizedTimestamp(pathPoints[0]?.properties.timestamp);
    this.endTimeStamp = this.getLocalizedTimestamp(pathPoints[pathPoints.length - 1]?.properties.timestamp);
  }

  handleSegmentPoints(timeLinePathPoint: TimeLinePathPoint) {
    this.points = timeLinePathPoint.pathPoints;
    const whiskersIndexes: Array<number> = this.getWhiskersIndexes([...this.points]);
    const upperWhisker = whiskersIndexes[0],
      lowerWhisker = whiskersIndexes[1];
    this.points = this.points.slice(upperWhisker, lowerWhisker);
    if (timeLinePathPoint.segmentIndex) this.points.shift();
    this.handleStartEndTime(this.points);
    this.changeDetectorRef.detectChanges();
  }

  viewEntireTrip() {
    this.leafletService.handleViewEntireTripControlClick();
    this.resetZoomValues();
  }

  toggleTime() {
    if (this.hoverIndex > -1) {
      if (this.hoverIndex == 0 || this.points.length - 1 === this.hoverIndex) {
        this.showTimestamps();
      } else {
        const percent = (this.hoverIndex / this.points.length) * 100;
        this.showStartTime = !(percent < 10);
        this.showEndTime = !(percent > 85);
      }
    } else {
      this.reset();
    }
  }

  reset() {
    this.hoverIndex = -1;
    this.showTimestamps();
    this.hoverEventCaptured = false;
  }

  showTimestamps() {
    this.showStartTime = true;
    this.showEndTime = true;
  }

  /* getWhiskersIndexes
   * The following method filters the indexes of the first and last event where the asset was poweredOn and Driving or Idling
   * @param {Array<TimeLinePoint>} all the events of the selected day
   * @return {Array<number>} the upper and lower whisker indexes
   */
  getWhiskersIndexes(points: Array<TimeLinePoint>): Array<number> {
    let upperWhiskerIndex: number;
    let lowerWhiskerIndex: number;
    for (let i = 0; i < points.length; i++) {
      if (
        points[i].powerOn == true &&
        (points[i].state == PathStatusTypes.DRIVING || points[i].state == PathStatusTypes.IDLING)
      ) {
        upperWhiskerIndex = i;
        break;
      }
    }
    for (let j = points.length - 1; j >= 0; j--) {
      if (
        points[j].powerOn == true &&
        (points[j]?.state == PathStatusTypes.DRIVING || points[j]?.state == PathStatusTypes.IDLING)
      ) {
        lowerWhiskerIndex = j;
        break;
      }
    }
    return [upperWhiskerIndex, lowerWhiskerIndex];
  }
  resetZoomValues() {
    if (this.zoomEnable) {
      this.zoomEnable = false;
      this.segmentSelected = false;
      this.handlePoints(this.pointsZoomAux);
      this.leafletService.zoomToFeatures(this.pointsZoomAux);
    }
  }
  selectZoomArea(i: number) {
    if (this.pointOnhoverZoom <= this.pointStartZoom && this.pointOnhoverZoom <= i) {
      for (let iterator = this.pointOnhoverZoom; iterator <= i; iterator++) {
        let pointIndex = document.getElementById(iterator.toString());
        pointIndex.classList.remove('zoom-select-area');
      }
      this.pointOnhoverZoom = i;
    } else {
      for (let iterator = i; iterator <= this.pointOnhoverZoom; iterator++) {
        let pointIndex = document.getElementById(iterator.toString());
        pointIndex.classList.remove('zoom-select-area');
      }
      this.pointOnhoverZoom = i;
    }
    if (this.selectEnable) {
      if (this.pointStartZoom <= i) {
        for (let iterator = this.pointStartZoom; iterator <= i; iterator++) {
          let pointIndex = document.getElementById(iterator.toString());
          pointIndex.classList.add('zoom-select-area');
        }
      } else {
        for (let iterator = i; iterator <= this.pointStartZoom; iterator++) {
          let pointIndex = document.getElementById(iterator.toString());
          pointIndex.classList.add('zoom-select-area');
        }
      }
    }
  }
  getActivityGlyph(status: string): string {
    switch (status?.toLowerCase()) {
      case 'driving':
        return 'U';
      case 'active':
        return 'V';
      case 'rest':
        return 'X';
      case 'passive':
        return 'W';
      default:
        return 'R';
    }
  }
}
